import { skipToken } from '@reduxjs/toolkit/query';
import type { ExchangeAssetMetricPoint } from '@src/providers/CoinMetricsApiProvider';
import { useCoinMetricsApiKey, useGetExchangeAssetMetricsQuery } from '@src/providers/CoinMetricsApiProvider';
import { useMemo } from 'react';
import {
  CM_MAX_PAGE_SIZE,
  exchangeFromPair,
  fundingRateFractionToPercent,
  isExchangeAssetPairFor,
  resolveExchangeDisplayName,
  useExchangeAssetCatalog,
} from '../core';
import { FUNDING_RATE_METRIC } from '../HistoricalFundingRate/tokens';
import type { FundingRateRow, FundingRateUpdateMode } from './FundingRateBlotter.types';
import {
  FUNDING_RATE_BLOTTER_METRICS,
  FUNDING_RATE_UPDATE_MODE_FREQUENCY,
  FUNDING_RATE_UPDATE_MODE_POLL_MS,
  OPEN_INTEREST_METRIC,
} from './tokens';

interface UseFundingRateBlotterDataParams {
  /** Configured CoinMetrics asset IDs (e.g. ['btc','eth']). Must be a stable reference. */
  cmAssetIds: string[];
  /** CoinMetrics asset id → Talos symbol, from the module's asset map. */
  symbolByCmAssetId: ReadonlyMap<string, string>;
  /** CoinMetrics exchange id → resolved display name, from useExchangeDisplayNameMap. */
  exchangeNameByExchangeId: ReadonlyMap<string, string>;
  /** Hourly or Daily — drives fetch frequency and polling interval. */
  updateMode: FundingRateUpdateMode;
  /** When false, no CoinMetrics requests fire — e.g. during initial setup, before creation is confirmed. */
  enabled: boolean;
}

interface UseFundingRateBlotterDataResult {
  /** One row per (asset, exchange), plus an empty row for any asset with no available pairs. */
  rows: FundingRateRow[];
  isLoading: boolean;
  isError: boolean;
  /** True when the catalog or timeseries query hit the pagination cap (some pairs may be missing). */
  isTruncated: boolean;
  /**
   * Metrics fetched alongside the funding rate (currently just open interest) that came back null
   * on every point in the current snapshot — see `detectUnentitledMetrics`. Empty until the
   * snapshot has resolved.
   */
  unentitledMetrics: string[];
}

const EMPTY_ROWS: FundingRateRow[] = [];
const EMPTY_CM_ASSET_IDS: string[] = [];
const EMPTY_UNENTITLED_METRICS: string[] = [];

/** Parses a CoinMetrics string value to a finite number, or null when absent/non-finite. */
function parseMetricValue(raw: string | null | undefined): number | null {
  if (raw == null) {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/**
 * `requiredMetrics` for which none of `points` carries a non-null value — the catalog-based
 * entitlement check (`useMissingMetrics` in the module) only covers the funding rate metric that
 * gates the asset universe, so it can't see a gap in a metric fetched alongside it (open interest).
 * CoinMetrics doesn't reject this multi-metric request outright when only one of the requested
 * metrics is unentitled for the key — mirroring `detectUnentitledLeaderboardMetrics` (Asset
 * Leaderboard), it silently omits the unentitled one from every returned point instead of erroring.
 * Only meaningful once `points` actually has data. Exported for unit testing.
 */
export function detectUnentitledMetrics(
  points: ExchangeAssetMetricPoint[] | undefined,
  requiredMetrics: readonly string[]
): string[] {
  if (points == null || points.length === 0) {
    return [];
  }
  return requiredMetrics.filter(metric => points.every(point => parseMetricValue(point[metric]) == null));
}

/**
 * Builds the normalized blotter rows from the resolved exchange-asset pairs and the latest
 * snapshot points. Assets are emitted in the configured order; each asset's pairs are sorted
 * alphabetically. An asset with no pairs yields a single empty row (catalog-aware availability).
 * A pair present in the catalog but missing from the response keeps its row with null cells.
 * Exported for unit testing.
 */
export function buildFundingRateBlotterRows({
  cmAssetIds,
  exchangeAssets,
  points,
  symbolByCmAssetId,
  exchangeNameByExchangeId,
}: {
  cmAssetIds: string[];
  exchangeAssets: string[];
  points: ExchangeAssetMetricPoint[] | undefined;
  symbolByCmAssetId: ReadonlyMap<string, string>;
  exchangeNameByExchangeId: ReadonlyMap<string, string>;
}): FundingRateRow[] {
  if (cmAssetIds.length === 0) {
    return EMPTY_ROWS;
  }

  // Keep the newest point per pair (limit_per_exchange_asset=1 should yield one, but be defensive).
  const latestByPair = new Map<string, ExchangeAssetMetricPoint>();
  for (const point of points ?? []) {
    const existing = latestByPair.get(point.exchange_asset);
    if (existing == null || new Date(point.time).getTime() > new Date(existing.time).getTime()) {
      latestByPair.set(point.exchange_asset, point);
    }
  }

  const rows: FundingRateRow[] = [];
  for (const cmAssetId of cmAssetIds) {
    const symbol = symbolByCmAssetId.get(cmAssetId) ?? cmAssetId.toUpperCase();
    const pairs = exchangeAssets.filter(ea => isExchangeAssetPairFor(ea, cmAssetId)).toSorted();

    if (pairs.length === 0) {
      rows.push({
        id: `${cmAssetId}-empty`,
        symbol,
        cmAssetId,
        exchange: undefined,
        exchangeDisplayName: undefined,
        fundingRatePct: null,
        openInterestUsd: null,
      });
      continue;
    }

    for (const pair of pairs) {
      const exchange = exchangeFromPair(pair, cmAssetId);
      const point = latestByPair.get(pair);
      const fundingRateFraction = parseMetricValue(point?.[FUNDING_RATE_METRIC]);
      rows.push({
        id: pair,
        symbol,
        cmAssetId,
        exchange,
        exchangeDisplayName: resolveExchangeDisplayName(exchange, exchangeNameByExchangeId),
        fundingRatePct: fundingRateFraction == null ? null : fundingRateFractionToPercent(fundingRateFraction),
        openInterestUsd: parseMetricValue(point?.[OPEN_INTEREST_METRIC]),
      });
    }
  }
  return rows;
}

/**
 * Resolves the available exchange-asset pairs for the configured assets from the CoinMetrics
 * catalog, fetches the latest funding-rate + open-interest value per pair, and builds the
 * normalized blotter rows. Refreshes on an interval determined by the update mode.
 */
export function useFundingRateBlotterData({
  cmAssetIds,
  symbolByCmAssetId,
  exchangeNameByExchangeId,
  updateMode,
  enabled,
}: UseFundingRateBlotterDataParams): UseFundingRateBlotterDataResult {
  const apiKey = useCoinMetricsApiKey();

  // Skip all requests when disabled by passing an empty asset list (both queries skip on that).
  const catalogAssetIds = enabled ? cmAssetIds : EMPTY_CM_ASSET_IDS;

  const {
    exchangeAssets,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    isTruncated: isCatalogTruncated,
  } = useExchangeAssetCatalog({ cmAssetIds: catalogAssetIds, metric: FUNDING_RATE_METRIC });

  const frequency = FUNDING_RATE_UPDATE_MODE_FREQUENCY[updateMode];
  // Only wildcard assets the catalog confirms have at least one pair — a configured asset with no
  // pairs (or a stale/delisted one) would otherwise send a wildcard that can't resolve to anything.
  const assetIdsWithPairs = useMemo(
    () => cmAssetIds.filter(id => exchangeAssets.some(ea => isExchangeAssetPairFor(ea, id))),
    [cmAssetIds, exchangeAssets]
  );
  // Query one wildcard per eligible asset (e.g. `*-btc,*-eth`) instead of enumerating every resolved
  // pair, so the request URL scales with the (small) asset count rather than the (potentially large)
  // pair count — avoids HTTP 414 on big baskets. CoinMetrics matches `*-btc` on the exact asset, the
  // same pairs the catalog resolves for the row set.
  const exchangeAssetsParam = useMemo(() => assetIdsWithPairs.map(id => `*-${id}`).join(','), [assetIdsWithPairs]);
  const metricsParam = useMemo(() => FUNDING_RATE_BLOTTER_METRICS.join(','), []);

  const queryArg =
    !apiKey || !enabled || isCatalogLoading || assetIdsWithPairs.length === 0
      ? skipToken
      : {
          exchange_assets: exchangeAssetsParam,
          metrics: metricsParam,
          frequency,
          limit_per_exchange_asset: 1,
          page_size: CM_MAX_PAGE_SIZE,
          paging_from: 'end' as const,
        };

  const { currentData, isError: isTimeseriesError } = useGetExchangeAssetMetricsQuery(queryArg, {
    pollingInterval: FUNDING_RATE_UPDATE_MODE_POLL_MS[updateMode],
  });

  // Hold rows until the catalog resolves, otherwise we'd briefly render an empty row for every
  // asset before availability is known.
  const rows = useMemo(
    () =>
      !enabled || isCatalogLoading
        ? EMPTY_ROWS
        : buildFundingRateBlotterRows({
            cmAssetIds,
            exchangeAssets,
            points: currentData?.data,
            symbolByCmAssetId,
            exchangeNameByExchangeId,
          }),
    [enabled, isCatalogLoading, cmAssetIds, exchangeAssets, currentData, symbolByCmAssetId, exchangeNameByExchangeId]
  );

  // Initial-load only: resolving the catalog, or pairs exist but the first snapshot hasn't arrived.
  // Excludes polling refetches (currentData persists across them) so the grid never flashes a loader
  // on refresh, and is false when disabled or with no assets configured (nothing to load).
  const isLoading =
    enabled &&
    cmAssetIds.length > 0 &&
    (isCatalogLoading || (exchangeAssets.length > 0 && currentData == null && !isTimeseriesError));

  const unentitledMetrics = useMemo(() => {
    const detected = detectUnentitledMetrics(currentData?.data, [OPEN_INTEREST_METRIC]);
    return detected.length === 0 ? EMPTY_UNENTITLED_METRICS : detected;
  }, [currentData]);

  return {
    rows,
    isLoading,
    isError: isCatalogError || isTimeseriesError,
    isTruncated: isCatalogTruncated || currentData?.truncated === true,
    unentitledMetrics,
  };
}
